import { all } from "@redux-saga/core/effects";
import { logError } from "ErrorService";
import { applyMiddleware, combineReducers, createStore } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import createSagaMiddleware from "redux-saga";
import {
  AuthAction,
  authReducer,
  AuthState,
  resetUser,
} from "State/Auth/AuthReducer";
import {
  guidepostReducer,
  GuidepostAction,
} from "State/Guidepost/GuidepostReducer";
import {
  getGuidepostSaga,
  postGuidepostLogAppVisitSaga,
} from "State/Guidepost/GuidepostSaga";
import {
  humanResourcesReducer,
  HumanResourcesAction,
} from "State/HumanResources/HumanResourcesReducer";
import { accessRolesReducer } from "State/AccessRoles/AccessRolesReducer";
import {
  getAccessRolesSaga,
  getAccessRoleDetailSaga,
  updateAccessRoleAccessRightsSaga,
} from "State/AccessRoles/AccessRolesSaga";
import {
  getHumanResourcesEmployeeDetailSaga,
  getHumanResourcesEmployeeAutocompleteSaga,
  getHumanResourcesPositionsAutocompleteSaga,
  getHumanResourcesDepartmentsAutocompleteSaga,
  getHumanResourcesCompanyAutocompleteSaga,
  saveHumanResourcesEmployeeDetailSaga,
  deleteHumanResourcesEmployeeSaga,
  getHumanResourcesEmployeeTypesSaga,
} from "State/HumanResources/HumanResourcesSaga";
import { getType } from "typesafe-actions";
import { nameof } from "Utils/ObjectUtils";
import { refreshAccessRightsSaga, signInSaga } from "./Auth/AuthSaga";
import { ticketReducer, TicketAction } from "./Tickets/TicketReducer";
import {
  changeTicketStatusSaga,
  getTicketsSaga,
  getEmployeeTicketsSaga,
} from "./Tickets/TicketSaga";
import { UserAction, userReducer } from "./Users/UserReducer";
import {
  deleteUserSaga,
  getUserDetailSaga,
  getUsersSaga,
  updateUserDetailSaga,
} from "./Users/UserSaga";
import { AppAction, appReducer } from "State/App/AppReducer";
import { CompanyAction, companyReducer } from "./Companies/CompanyReducer";
import {
  InvoiceItemCategoriesAction,
  invoiceItemCategoriesReducer,
} from "./InvoiceItemCategories/InvoiceItemCategoriesReducer";
import {
  getInvoiceItemCategoriesSaga,
  getInvoiceItemCategoryDetailSaga,
  saveInvoiceItemCategorySaga,
  getDivisionsSaga,
} from "./InvoiceItemCategories/InvoiceItemCategoriesSaga";
import {
  getCompaniesSaga,
  deleteCompanySaga,
  getCompanyDetailSaga,
  saveCompanyDetailSaga,
  saveCompanyContactPersonSaga,
  deleteCompanyContactPersonSaga,
} from "./Companies/CompanySaga";

const authPersistConfig = {
  key: "auth",
  storage: storage,
  blacklist: [nameof<AuthState>("isLoading"), nameof<AuthState>("errorCode")],
};

/**
 * Create global state of application
 */
const combinedReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  users: userReducer,
  companies: companyReducer,
  guidepost: guidepostReducer,
  tickets: ticketReducer,
  humanResources: humanResourcesReducer,
  accessRoles: accessRolesReducer,
  invoiceItemCategories: invoiceItemCategoriesReducer,
  app: appReducer,
});

/**
 * Type of global application state
 */
export type RootStateType = ReturnType<typeof combinedReducer>;

/**
 * All changes, that global application state can handle
 */
export type RootActionType =
  | AuthAction
  | UserAction
  | CompanyAction
  | GuidepostAction
  | HumanResourcesAction
  | TicketAction
  | InvoiceItemCategoriesAction
  | AppAction;

const rootReducer = (state: RootStateType, action: RootActionType) => {
  if (action.type === getType(resetUser)) {
    state = (undefined as unknown) as RootStateType;
    localStorage.clear();
  }

  return combinedReducer(state, action);
};
const persistConfig = {
  key: "root",
  storage,
  blacklist: ["auth", "app"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer as any);

const sagaMiddleware = createSagaMiddleware({
  onError: (error, errorInfo) => {
    logError(error, errorInfo);
  },
});

const middlewares = [sagaMiddleware];

const store = createStore(
  persistedReducer,
  composeWithDevTools(applyMiddleware(...middlewares)),
);

function* rootSaga() {
  yield all([
    signInSaga(),
    refreshAccessRightsSaga(),
    getUsersSaga(),
    getUserDetailSaga(),
    deleteUserSaga(),
    updateUserDetailSaga(),
    getCompaniesSaga(),
    deleteCompanySaga(),
    getCompanyDetailSaga(),
    saveCompanyDetailSaga(),
    getGuidepostSaga(),
    getTicketsSaga(),
    changeTicketStatusSaga(),
    getEmployeeTicketsSaga(),
    getHumanResourcesEmployeeDetailSaga(),
    getHumanResourcesEmployeeAutocompleteSaga(),
    getHumanResourcesPositionsAutocompleteSaga(),
    getHumanResourcesDepartmentsAutocompleteSaga(),
    getHumanResourcesCompanyAutocompleteSaga(),
    saveHumanResourcesEmployeeDetailSaga(),
    deleteHumanResourcesEmployeeSaga(),
    postGuidepostLogAppVisitSaga(),
    getAccessRolesSaga(),
    getAccessRoleDetailSaga(),
    updateAccessRoleAccessRightsSaga(),
    saveCompanyContactPersonSaga(),
    deleteCompanyContactPersonSaga(),
    getHumanResourcesEmployeeTypesSaga(),
    getInvoiceItemCategoriesSaga(),
    getInvoiceItemCategoryDetailSaga(),
    saveInvoiceItemCategorySaga(),
    getDivisionsSaga(),
  ]);
}

sagaMiddleware.run(rootSaga);

export const useApplicationStore = () => {
  (window as any).applicationStore = store;
  let persistor = persistStore(store as any);
  return { store, persistor };
};
